"use client";

import { Section } from "../types";
import { useFormState } from "./useFormState";
import { useComponentData } from "./useComponentData";
import { useCategoryActions } from "./useCategoryActions";
import { useNestedItemActions } from "./useNestedItemActions";
import { useItemActions } from "./useItemActions";

export interface UseForm3Props {
  selectedSection: Section;
  onSave: (section: Section) => void;
  pageId?: string;
  pageComponentId?: string;
  variantDetails?: any; // Passed from parent to avoid circular dependency
}

export const useForm3 = ({ selectedSection, onSave, pageId, pageComponentId, variantDetails }: UseForm3Props) => {
  // Core form state and config
  const {
    formState,
    setFormState,
    dispatch,
    variantConfig,
    currentUser,
    reduxState,
    componentFieldsConfig,
    categoryFieldsConfig,
    itemFieldsConfig,
    nestedItemFieldsConfig,
    prevPageComponentIdRef,
  } = useFormState({ selectedSection, pageId, pageComponentId, variantDetails });

  // Component data management
  const { fetchData, handleComponentFieldsUpdate, handleComponentSave, ensureComponentParent } =
    useComponentData({
    selectedSection,
    formState,
    setFormState,
    pageId,
    pageComponentId,
    variantConfig,
    componentFieldsConfig,
    dispatch,
    reduxState,
    currentUserId: currentUser?.id,
    onSave,
    prevPageComponentIdRef,
  });

  // Category actions
  const {
    handleCategoryClick,
    handleNewCategory,
    handleCategorySave,
    handleCategoryDelete,
    toggleCategoryExpand,
  } = useCategoryActions({
    selectedSection,
    formState,
    setFormState,
    pageId,
    pageComponentId,
    variantConfig,
    categoryFieldsConfig,
    currentUserId: currentUser?.id,
    onSave,
    fetchData,
    ensureComponentParent,
  });

  // Nested item (semester) actions
  const {
    handleNestedItemClick,
    handleNewNestedItem,
    handleNestedItemSave,
    handleNestedItemDelete,
    toggleNestedExpand,
  } = useNestedItemActions({
    formState,
    setFormState,
    pageComponentId,
    variantConfig,
    nestedItemFieldsConfig,
    currentUserId: currentUser?.id,
    fetchData,
  });

  // Item actions
  const { handleItemClick, handleNewItem, handleItemSave, handleItemDelete } = useItemActions({
    selectedSection,
    formState,
    setFormState,
    pageComponentId,
    variantConfig,
    itemFieldsConfig,
    currentUserId: currentUser?.id,
    reduxState,
    onSave,
    fetchData,
    ensureComponentParent,
  });

  // Form field update handler
  const handleFormFieldsUpdate = (updatedData: Record<string, any>) => {
    setFormState((prev) => ({
      ...prev,
      formFields: { ...prev.formFields, ...updatedData },
    }));
  };

  // Cancel form handler
  const handleCancelForm = () => {
    setFormState((prev) => ({
      ...prev,
      selectedItemType: null,
      selectedCategoryId: null,
      selectedNestedId: null,
      selectedItemId: null,
      editingItemId: null,
      formFields: {},
      formFieldErrors: {},
      formFieldMessages: {},
      categoryMembershipIds: [],
    }));
  };

  // Determine which save handler to use based on selected item type
  const handleSave = () => {
    switch (formState.selectedItemType) {
      case "category":
        return handleCategorySave();
      case "nested":
        return handleNestedItemSave();
      case "item":
        return handleItemSave();
      default:
        return Promise.resolve();
    }
  };

  // Check if nested items are supported
  const hasNestedItemsSupport = Boolean((variantConfig as any)?.fetchNestedItemsMethod);

  return {
    // State
    formState,
    setFormState,
    variantConfig,
    reduxState,

    // Configs
    componentFieldsConfig,
    categoryFieldsConfig,
    itemFieldsConfig,
    nestedItemFieldsConfig,

    // Feature flags
    hasNestedItemsSupport,

    // Component handlers
    handleComponentFieldsUpdate,
    handleComponentSave,

    // Category handlers
    handleCategoryClick,
    handleNewCategory,
    handleCategorySave,
    handleCategoryDelete,
    toggleCategoryExpand,

    // Nested item handlers
    handleNestedItemClick,
    handleNewNestedItem,
    handleNestedItemSave,
    handleNestedItemDelete,
    toggleNestedExpand,

    // Item handlers
    handleItemClick,
    handleNewItem,
    handleItemSave,
    handleItemDelete,

    // Form handlers
    handleFormFieldsUpdate,
    handleCancelForm,
    handleSave,
  };
};

// Re-export individual hooks
export { useFormState } from "./useFormState";
export { useComponentData } from "./useComponentData";
export { useCategoryActions } from "./useCategoryActions";
export { useNestedItemActions } from "./useNestedItemActions";
export { useItemActions } from "./useItemActions";
export * from "./types";

